import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import { VerifyEmail, ForgotPassword, ResetPassword, AcceptInvite } from "@/pages/AuthExtras";
import Terms from "@/pages/Terms";
import FAQ from "@/pages/FAQ";
import Layout from "@/pages/Layout";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Photographers from "@/pages/Photographers";
import Seasons from "@/pages/Seasons";
import Competitions from "@/pages/Competitions";
import LiveEntry from "@/pages/LiveEntry";
import Results from "@/pages/Results";
import Stats from "@/pages/Stats";
import Users from "@/pages/Users";
import Platform from "@/pages/Platform";
import Settings from "@/pages/Settings";
import Awards from "@/pages/Awards";
import Billing from "@/pages/Billing";
import BlogIndex from "@/pages/Blog";
import PostSpreadsheetsFail from "@/pages/blog/PostSpreadsheetsFail";
import PostScoreEntry from "@/pages/blog/PostScoreEntry";
import PostOrganising from "@/pages/blog/PostOrganising";
import PostSecretary from "@/pages/blog/PostSecretary";
import PostJudging from "@/pages/blog/PostJudging";
import PostPDI from "@/pages/blog/PostPDI";
import PostPrint from "@/pages/blog/PostPrint";
import PostLeague from "@/pages/blog/PostLeague";
import PostSoftware from "@/pages/blog/PostSoftware";
import PostTroubleshoot from "@/pages/blog/PostTroubleshoot";
import PrintScoresheet from "@/pages/PrintScoresheet";
import PrintResults from "@/pages/PrintResults";
import MyEntries from "@/pages/MyEntries";
import { PortalHome, PortalCompetition } from "@/pages/Portal";
import "@/App.css";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-12 text-neutral-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    // SUPERADMIN can reach everything except member-only paths.
    if (user.role === "SUPERADMIN") return children;
    if (user.role === "MEMBER") return <Navigate to="/me" replace />;
    return <Navigate to="/" replace />;
  }
  return children;
}

function MemberOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-12 text-neutral-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RoleRouter({ children }) {
  // After login, send MEMBER to /me, everyone else to standard layout.
  // Logged-out visitors land on the marketing page instead of being
  // immediately bounced to /login.
  const { user, loading } = useAuth();
  if (loading) return <div className="p-12 text-neutral-400">Loading…</div>;
  if (!user) return <Landing />;
  if (user.role === "MEMBER") return <Navigate to="/me" replace />;
  return children;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/why-spreadsheets-fail-camera-clubs" element={<PostSpreadsheetsFail />} />
          <Route path="/blog/score-a-competition-in-5-minutes" element={<PostScoreEntry />} />
          <Route path="/blog/live-judging-in-5-minutes" element={<Navigate to="/blog/score-a-competition-in-5-minutes" replace />} />
          <Route path="/blog/organising-a-camera-club-competition" element={<PostOrganising />} />
          <Route path="/blog/competition-secretary-field-manual" element={<PostSecretary />} />
          <Route path="/blog/judging-and-scoring-photography-competitions" element={<PostJudging />} />
          <Route path="/blog/pdi-projected-digital-image-competitions" element={<PostPDI />} />
          <Route path="/blog/print-competitions-without-losing-prints" element={<PostPrint />} />
          <Route path="/blog/season-league-tables-and-photographer-of-the-year" element={<PostLeague />} />
          <Route path="/blog/what-to-look-for-in-camera-club-competition-software" element={<PostSoftware />} />
          <Route path="/blog/troubleshooting-and-best-practices" element={<PostTroubleshoot />} />
          {/* Public portal — no auth */}
          <Route path="/portal" element={<PortalHome />} />
          <Route path="/portal/competition/:competitionId" element={<PortalCompetition />} />

          {/* Member self-service */}
          <Route path="/me" element={<MemberOnly><MyEntries /></MemberOnly>} />

          {/* Print views (any signed-in user) */}
          <Route
            path="/print/scoresheet/:competitionId"
            element={<MemberOnly><PrintScoresheet /></MemberOnly>}
          />
          <Route
            path="/print/results/:competitionId"
            element={<MemberOnly><PrintResults /></MemberOnly>}
          />

          {/* Console (ADMIN/SECRETARY/VIEWER) */}
          <Route
            path="/"
            element={
              <RoleRouter>
                <Layout />
              </RoleRouter>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="photographers" element={<Photographers />} />
            <Route path="seasons" element={<Seasons />} />
            <Route path="competitions" element={<Competitions />} />
            <Route path="entry/:competitionId" element={<LiveEntry />} />
            <Route path="results" element={<Results />} />
            <Route
              path="stats"
              element={
                <Protected roles={["ADMIN", "SECRETARY"]}>
                  <Stats />
                </Protected>
              }
            />
            <Route
              path="awards"
              element={
                <Protected roles={["ADMIN", "SECRETARY"]}>
                  <Awards />
                </Protected>
              }
            />
            <Route
              path="users"
              element={
                <Protected roles={["ADMIN"]}>
                  <Users />
                </Protected>
              }
            />
            <Route
              path="settings"
              element={
                <Protected roles={["ADMIN"]}>
                  <Settings />
                </Protected>
              }
            />
            <Route
              path="billing"
              element={
                <Protected roles={["ADMIN"]}>
                  <Billing />
                </Protected>
              }
            />
            <Route
              path="platform"
              element={
                <Protected roles={["SUPERADMIN"]}>
                  <Platform />
                </Protected>
              }
            />
            <Route
              path="platform/subscribers"
              element={
                <Protected roles={["SUPERADMIN"]}>
                  <Platform />
                </Protected>
              }
            />
            <Route
              path="platform/assets"
              element={
                <Protected roles={["SUPERADMIN"]}>
                  <Platform />
                </Protected>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
